import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, CircularProgress, Button, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StripeService from '../services/StripeService';
import PricingCard from './sub-components/PricingCard';
import logo from '../images/CRM-Logo-large.png';

const PricingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [error, setError] = useState(null);
    const [selectingPlanId, setSelectingPlanId] = useState(null);

    const fetchPlans = async () => {
        setLoadingPlans(true);
        setError(null);
        try {
            const data = await StripeService.getProducts();
            const mapped = data.map(product => {
                const price = product.prices?.[0];
                const isFree = !price || price.unitAmount === 0 || price.unitAmount === null;
                return {
                    ...product,
                    isFree,
                    price: isFree ? 0 : (price.unitAmount / 100).toFixed(2),
                    currency: price?.currency?.toUpperCase() ?? '',
                    priceId: price?.id ?? null,
                    interval: price?.interval ?? null,
                    marketingFeatures: product.marketingFeatures ?? [],
                };
            });
            setPlans(mapped);
        } catch {
            setError(t('pricing.errorLoading'));
        } finally {
            setLoadingPlans(false);
        }
    };

    useEffect(() => {
        fetchPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelect = async (plan) => {
        setSelectingPlanId(plan.id);
        try {
            if (plan.isFree) {
                await StripeService.selectFreePlan();
                navigate('/home');
            } else {
                await StripeService.createCheckoutSession(plan.priceId);
            }
        } catch {
            setSelectingPlanId(null);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', py: 8 }}>
            <Container maxWidth="md">
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <img src={logo} alt="Plankton CRM" style={{ width: '180px', height: 'auto', marginBottom: 24 }} />
                    <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: '#1a2e3b' }}>
                        {t('pricing.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
                        {t('pricing.subtitle')}
                    </Typography>
                </Box>

                {loadingPlans && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: '#427898' }} />
                    </Box>
                )}

                {error && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
                        <Button variant="outlined" onClick={fetchPlans} sx={{ borderColor: '#427898', color: '#427898' }}>
                            {t('pricing.retry')}
                        </Button>
                    </Box>
                )}

                {!loadingPlans && !error && (
                    <Grid container spacing={3} justifyContent="center">
                        {plans.map(plan => (
                            <Grid item xs={12} sm={6} md={plans.length <= 3 ? 12 / plans.length : 4} key={plan.id}
                                sx={{ display: 'flex', flexDirection: 'column' }}>
                                <PricingCard
                                    plan={plan}
                                    onSelect={handleSelect}
                                    loading={selectingPlanId === plan.id}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>
        </Box>
    );
};

export default PricingPage;